import type { CSSProperties } from 'react'

import Markup from '~/client/src/shared/models/Markup'

import { DEFAULT_MARKUP_STYLE, NO_FILL } from './MarkupDrawController'
import { DEFAULT_FONT_SIZE, ITextBox, TextAlign } from './MarkupTextStore'

export const TEXT_MARKUP_FONT_FAMILY = '"Inter Variable", sans-serif'
export const TEXT_LINE_HEIGHT = 1.3
// Inside the box's scale, so it grows and shrinks with the text.
export const TEXT_PADDING_PX = 4

/** Everything that decides how a text markup looks, with defaults filled in. */
export interface ITextMarkupSpec {
  text: string
  fontSize: number
  bold: boolean
  italic: boolean
  underline: boolean
  color: string
  /** NO_FILL = transparent. */
  fillColor: string
  textAlign: TextAlign
  /** null = auto-width to content. */
  boxWidth: number | null
}

export function specOfMarkup(markup: Markup): ITextMarkupSpec {
  return {
    text: markup.text ?? '',
    fontSize: markup.fontSize ?? DEFAULT_FONT_SIZE,
    bold: markup.bold ?? false,
    italic: markup.italic ?? false,
    underline: markup.underline ?? false,
    color: markup.color ?? DEFAULT_MARKUP_STYLE.color,
    fillColor: markup.fillColor ?? NO_FILL,
    textAlign: (markup.textAlign as TextAlign) ?? 'left',
    boxWidth: markup.boxWidth ?? null,
  }
}

export function specOfBox(box: ITextBox): ITextMarkupSpec {
  return {
    text: box.text,
    fontSize: box.fontSize,
    bold: box.bold,
    italic: box.italic,
    underline: box.underline,
    color: box.color,
    fillColor: box.fillColor,
    textAlign: box.textAlign,
    boxWidth: box.boxWidth,
  }
}

/** A text markup as the GL layer takes it, from a saved markup or an editor box alike. */
export interface ITextMarkupInput {
  id: string
  lng: number
  lat: number
  // Degrees clockwise from north.
  rotation: number
  spec: ITextMarkupSpec
}

export function inputOfMarkup(markup: Markup): ITextMarkupInput {
  return {
    id: markup.id,
    lng: markup.coordinates[0].longitude,
    lat: markup.coordinates[0].latitude,
    rotation: markup.rotation ?? 0,
    spec: specOfMarkup(markup),
  }
}

export function inputOfBox(box: ITextBox): ITextMarkupInput {
  return {
    id: box.id,
    lng: box.lng,
    lat: box.lat,
    rotation: box.rotation,
    spec: specOfBox(box),
  }
}

// Canvas font shorthand for the face a spec renders in.
export function textMarkupFont(spec: ITextMarkupSpec): string {
  return `${spec.italic ? 'italic ' : ''}${spec.bold ? 700 : 400} ${spec.fontSize}px ${TEXT_MARKUP_FONT_FAMILY}`
}

// The one CSS source for a text markup's text, shared by the saved view and the editor box.
// Inherited text properties are set explicitly so the result does not depend on the host.
// boxWidth is the content width: content-box keeps padding out of it.
export function textMarkupBoxStyle(spec: ITextMarkupSpec): CSSProperties {
  return {
    boxSizing: 'content-box',
    fontFamily: TEXT_MARKUP_FONT_FAMILY,
    fontSize: spec.fontSize,
    lineHeight: TEXT_LINE_HEIGHT,
    fontWeight: spec.bold ? 700 : 400,
    fontStyle: spec.italic ? 'italic' : 'normal',
    textDecoration: spec.underline ? 'underline' : 'none',
    color: spec.color,
    textAlign: spec.textAlign,
    width: spec.boxWidth ?? 'max-content',
    whiteSpace: spec.boxWidth == null ? 'pre' : 'pre-wrap',
    wordBreak: spec.boxWidth == null ? 'normal' : 'break-word',
    letterSpacing: 'normal',
    wordSpacing: 'normal',
    fontKerning: 'auto',
    fontFeatureSettings: 'normal',
    textRendering: 'auto',
  }
}
